import type { SchemaInput, SchemaRecord } from './schema-input.js';
import { isString } from './runtime-values.js';
import { SERVICE_AREA_WIDGET_PLAN } from './plans.js';

const premiumFeatures = [
  'auto_outline',
  'generic_pro',
  'illustrated_markers',
  'password_protection',
  'collaborators',
  'embed_branding',
  'exact_radius',
  'png_export',
  'pdf_export',
  'map_library',
  'ai_map_builder',
  'satellite_basemap',
  'drawing_layers',
  'data_layers',
  'pasted_images',
  SERVICE_AREA_WIDGET_PLAN,
] as const;

const premiumSources = [
  'auto_outline_tool',
  'ai_chat_images',
  'marker_picker',
  'account_menu',
  'my_maps_password',
  'my_maps_collaborators',
  'embed_branding_note',
  'dataset_detail',
  'public_navigation',
  'public_footer',
  'export_landing',
  'map_type_control',
  'upgrade_dialog',
  'pro_page',
  'checkout_button',
  'portal_button',
  'exact_radius_btn',
  'map_limit',
  'share_dialog',
  'toolbar_download',
  'printable_export',
  'email_capture',
  'export_preview',
  'us_map_action',
  'service_area_builder',
  'service_area_radius',
  'service_area_circle',
  'service_area_landing',
  'service_area_map',
  'service_area_embed',
  'service_area_publish',
  'ai_map_helper',
  'checkout_recovery',
  'layers_panel',
  'data_layer_save',
  'map_canvas',
] as const;

const userPlans = ['anonymous', 'paid_guest', 'free', 'pro', SERVICE_AREA_WIDGET_PLAN, 'unknown'] as const;
const booleanMetadata = ['true', 'false'] as const;
const checkoutModes = ['subscription', 'payment'] as const;
const purchases = ['png_one_time', 'pdf_one_time'] as const;
const promotions = ['service_area_first_month_free'] as const;
const serviceAreaExperiments = ['control', 'skip_adjust'] as const;
const exportOfferExperiments = ['control', 'one_time_first'] as const;
const checkoutExperiences = ['embedded_tax_inclusive'] as const;

export const SERVICE_AREA_FIRST_MONTH_FREE_PROMOTION = promotions[0];
export const SERVICE_AREA_FIRST_MONTH_FREE_DAYS = 30;
export const SERVICE_AREA_JOURNEY_ID_PATTERN = /^[a-f0-9-]{36}$/;
export type PremiumSource = typeof premiumSources[number];
export type ServiceAreaExperimentVariant = typeof serviceAreaExperiments[number];
export type CheckoutExperience = typeof checkoutExperiences[number];

export interface CheckoutAttribution {
  feature?: string;
  source?: string;
  user_plan?: string;
  coupon_active?: string;
  checkout_mode?: string;
  purchase?: string;
  checkout_attempt_id?: string;
  variant?: string;
  checkout_experience_variant?: string;
  export_flow_id?: string;
  offer_version?: string;
  popup_experiment?: string;
  popup_variant?: string;
  popup_exposure_id?: string;
  internal_test?: 'true';
}

export const PREMIUM_FEATURE_VALUES = new Set<string>(premiumFeatures);
export const PREMIUM_SOURCE_VALUES = new Set<string>(premiumSources);
export const USER_PLAN_VALUES = new Set<string>(userPlans);
export const COUPON_ACTIVE_VALUES = new Set<string>(booleanMetadata);
export const CHECKOUT_MODE_VALUES = new Set<string>(checkoutModes);
export const PURCHASE_VALUES = new Set<string>(purchases);
export const PROMOTION_VALUES = new Set<string>(promotions);
export const SERVICE_AREA_EXPERIMENT_VALUES = new Set<string>(serviceAreaExperiments);
export const EXPORT_OFFER_VERSION = 'export_output_v2';
export const EXPORT_OFFER_VERSION_VALUES = new Set<string>(['export_output_v1', EXPORT_OFFER_VERSION]);
export const EXPORT_OFFER_EXPERIMENT_VALUES = new Set<string>(exportOfferExperiments);
export const DEFAULT_CHECKOUT_EXPERIENCE: CheckoutExperience = 'embedded_tax_inclusive';
export const CHECKOUT_EXPERIENCE_VALUES = new Set<string>(checkoutExperiences);
export const EXPORT_CHECKOUT_LAYOUT_VALUES = new Set<string>(['checkout_popup_v1', 'checkout_preview_v1']);
export const CHECKOUT_EXPERIMENT_VALUES = new Set<string>([
  ...serviceAreaExperiments,
  ...exportOfferExperiments,
  ...EXPORT_CHECKOUT_LAYOUT_VALUES,
]);

export function cleanMetadataValue(value: SchemaInput, allowlist: ReadonlySet<string>): string | null {
  return isString(value) && allowlist.has(value) ? value : null;
}

export function isOneTimeExportCheckout(metadata: SchemaRecord | undefined): boolean {
  const feature = cleanMetadataValue(metadata?.feature, PREMIUM_FEATURE_VALUES);
  const purchase = cleanMetadataValue(metadata?.purchase, PURCHASE_VALUES);
  return cleanMetadataValue(metadata?.checkout_mode, CHECKOUT_MODE_VALUES) === 'payment'
    && ((feature === 'png_export' && purchase === 'png_one_time')
      || (feature === 'pdf_export' && purchase === 'pdf_one_time'));
}
